import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapPin, Navigation, Shield, Map, Building2, Link2, Store } from 'lucide-react';
import { parse2GisLink } from '../../utils/parse2GisLink';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectOption } from '../ui';
import { LoadingSpinner } from '../ui/loading-states';
import { useCreateLocation, useUpdateLocation } from '../../hooks/locations/useLocations';
import { useGeolocation } from '../../hooks/locations/useGeolocation';
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations';
import { useDepartments } from '../../hooks/useDepartments';
import {
  useLocationForecastMapping,
  useLocationDepartments,
  useSetLocationForecastMapping,
  useSetLocationDepartments,
} from '../../hooks/locations/useLocationPointConfig';
import LazyLocationMap from './LazyLocationMap';
import type { Location, CreateLocationDto } from '../../types/location.types';

interface LocationFormProps {
  location: Location | null;
  isOpen: boolean;
  onClose: () => void;
}

// Тип источника координат для приоритетной логики
type CoordinatesSource = 'manual' | 'map' | 'geo' | '2gis' | null;

// Статус парсинга 2GIS ссылки
type GisLinkStatus = 'idle' | 'success' | 'error';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const LocationForm: React.FC<LocationFormProps> = ({
  location,
  isOpen,
  onClose,
}) => {
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const { data: organizations } = useOrganizationsForSelect();

  const [formData, setFormData] = useState<CreateLocationDto>({
    name: '',
    latitude: 0,
    longitude: 0,
    radius_meters: 50,
    address: '',
    description: '',
    requires_gps_validation: false,
    max_gps_deviation_meters: 25,
    is_active: true,
    organization_id: undefined,
  });


  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [tempCoordinates, setTempCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);

  // Состояние для 2GIS ссылки
  const [gisLink, setGisLink] = useState('');
  const [gisLinkStatus, setGisLinkStatus] = useState<GisLinkStatus>('idle');
  const [coordinatesSource, setCoordinatesSource] = useState<CoordinatesSource>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { getCurrentPosition, loading: geoLoading, error: geoError, permissionDenied } = useGeolocation();

  // === Точка продаж (aqniet/iiko) и подразделения точки (только при редактировании) ===
  const { data: forecastMapping } = useLocationForecastMapping(location?.id);
  const { data: locationDepartments } = useLocationDepartments(location?.id);
  const setForecastMappingMutation = useSetLocationForecastMapping();
  const setDepartmentsMutation = useSetLocationDepartments();
  // Кандидаты для мультиселекта: подразделения организации локации
  // (или все подразделения тенанта, если организация не указана)
  const { data: departmentsForPick } = useDepartments({
    organization_id: formData.organization_id || undefined,
    limit: 500,
  });

  const [forecastDeptId, setForecastDeptId] = useState('');
  const [forecastDeptName, setForecastDeptName] = useState('');
  const [selectedDeptIds, setSelectedDeptIds] = useState<Set<string>>(new Set());

  // Собственный override локации; унаследованное от организации показываем подсказкой
  const initialForecastId =
    forecastMapping?.source === 'override'
      ? (forecastMapping.forecast_department_id ?? '')
      : '';
  const initialForecastName =
    forecastMapping?.source === 'override'
      ? (forecastMapping.forecast_department_name ?? '')
      : '';
  const initialDeptIds = useMemo(
    () => new Set((locationDepartments ?? []).map((d) => d.id)),
    [locationDepartments]
  );

  useEffect(() => {
    setForecastDeptId(initialForecastId);
    setForecastDeptName(initialForecastName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastMapping]);

  useEffect(() => {
    setSelectedDeptIds(new Set(initialDeptIds));
  }, [initialDeptIds]);

  const toggleDepartment = (id: string) => {
    setSelectedDeptIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const setsEqual = (a: Set<string>, b: Set<string>) =>
    a.size === b.size && [...a].every((x) => b.has(x));

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        radius_meters: location.radius_meters,
        address: location.address || '',
        description: location.description || '',
        requires_gps_validation: location.requires_gps_validation,
        max_gps_deviation_meters: location.max_gps_deviation_meters || 25,
        is_active: location.is_active,
        organization_id: location.organization_id || undefined,
      });
    }
  }, [location]);

  // Инициализируем временные координаты при открытии модального окна карты
  useEffect(() => {
    if (isMapModalOpen) {
      setTempCoordinates({
        latitude: formData.latitude,
        longitude: formData.longitude,
      });
    } else {
      setTempCoordinates(null);
    }
  }, [isMapModalOpen, formData.latitude, formData.longitude]);

  // Debounce обработка ссылки 2GIS (600ms)
  useEffect(() => {
    // Очищаем предыдущий таймер
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Если ссылка пустая, сбрасываем статус
    if (!gisLink.trim()) {
      setGisLinkStatus('idle');
      return;
    }

    // Устанавливаем новый таймер
    debounceTimerRef.current = setTimeout(() => {
      const coords = parse2GisLink(gisLink);

      if (coords) {
        // Координаты найдены - проверяем приоритет источника
        // 2GIS автозаполнение работает только если:
        // - источник не установлен (null)
        // - источник уже был 2gis (обновление ссылки)
        if (coordinatesSource === null || coordinatesSource === '2gis') {
          setFormData((prev) => ({
            ...prev,
            latitude: coords.latitude,
            longitude: coords.longitude,
          }));
          setCoordinatesSource('2gis');
          setGisLinkStatus('success');

          // Очищаем ошибки координат, если были
          setErrors((prev) => {
            const { latitude: _lat, longitude: _lng, ...rest } = prev;
            return rest;
          });
        } else {
          // Источник уже установлен явно (карта или геолокация)
          // Показываем успех, но не перезаписываем координаты
          setGisLinkStatus('success');
        }
      } else {
        // Не удалось распарсить
        setGisLinkStatus('error');
      }
    }, 600);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [gisLink, coordinatesSource]);

  const handleGetCurrentLocation = async () => {
    try {
      const position = await getCurrentPosition();
      setFormData((prev) => ({
        ...prev,
        latitude: position.latitude,
        longitude: position.longitude,
      }));
      // Устанавливаем источник координат (высокий приоритет)
      setCoordinatesSource('geo');
      // Очищаем ошибки, если были
      if (errors.location) {
        setErrors((prev) => ({ ...prev, location: '' }));
      }

      // Показываем информационное сообщение, если использовались fallback координаты
      if (position.accuracy >= 1000) {
        console.info('Использованы координаты по умолчанию для Алматы');
      }
    } catch (error) {
      // Эта ошибка не должна возникать, так как мы всегда возвращаем fallback
      console.error('Unexpected geolocation error:', error);
      setErrors({ location: geoError || 'Не удалось получить местоположение' });
    }
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setTempCoordinates({ latitude: lat, longitude: lng });
  };

  const handleConfirmLocationSelection = () => {
    if (tempCoordinates) {
      setFormData((prev) => ({
        ...prev,
        latitude: tempCoordinates.latitude,
        longitude: tempCoordinates.longitude,
      }));
      // Устанавливаем источник координат (высший приоритет)
      setCoordinatesSource('map');
      setTempCoordinates(null);
      setIsMapModalOpen(false);
    }
  };

  const handleCancelLocationSelection = () => {
    setTempCoordinates(null);
    setIsMapModalOpen(false);
  };

  // Обработчик ручного изменения координат
  const handleManualCoordinateChange = useCallback(
    (field: 'latitude' | 'longitude', value: number) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Устанавливаем источник как ручной ввод
      setCoordinatesSource('manual');
    },
    []
  );

  // Обработчик изменения ссылки 2GIS
  const handleGisLinkChange = useCallback((value: string) => {
    setGisLink(value);
    // Сбрасываем статус при изменении ссылки
    if (gisLinkStatus !== 'idle') {
      setGisLinkStatus('idle');
    }
  }, [gisLinkStatus]);

  const handleRadiusChange = (newRadius: number) => {
    setFormData((prev) => ({
      ...prev,
      radius_meters: newRadius,
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Название обязательно';
    }

    if (formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'Широта должна быть от -90 до 90';
    }

    if (formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'Долгота должна быть от -180 до 180';
    }

    if (formData.radius_meters < 10 || formData.radius_meters > 1000) {
      newErrors.radius_meters = 'Радиус должен быть от 10 до 1000 метров';
    }

    if (forecastDeptId.trim() && !UUID_REGEX.test(forecastDeptId.trim())) {
      newErrors.forecast_department_id = 'Некорректный UUID точки продаж';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      if (location) {
        await updateMutation.mutateAsync({ id: location.id, data: formData });

        // Точка продаж: PUT только при изменении собственного override
        const trimmedId = forecastDeptId.trim();
        const trimmedName = forecastDeptName.trim();
        if (
          trimmedId !== initialForecastId ||
          (trimmedId && trimmedName !== initialForecastName)
        ) {
          await setForecastMappingMutation.mutateAsync({
            locationId: location.id,
            forecast_department_id: trimmedId || null,
            forecast_department_name: trimmedId ? trimmedName || null : null,
          });
        }

        // Подразделения точки: PUT только при изменении списка
        if (!setsEqual(selectedDeptIds, initialDeptIds)) {
          await setDepartmentsMutation.mutateAsync({
            locationId: location.id,
            department_ids: [...selectedDeptIds],
          });
        }
      } else {
        await createMutation.mutateAsync(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving location:', error);
      const message =
        (error as { message?: string })?.message ||
        'Не удалось сохранить локацию';
      setErrors((prev) => ({ ...prev, submit: message }));
    }
  };

  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    setForecastMappingMutation.isPending ||
    setDepartmentsMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {location ? 'Редактировать локацию' : 'Новая локация'}
          </DialogTitle>
          <DialogDescription>
            {location
              ? 'Измените параметры локации и настройки контроля'
              : 'Создайте новую точку контроля посещаемости'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Основные данные */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Название *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Главный офис"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <Label htmlFor="address">Адрес</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="ул. Примерная, 123"
              />
            </div>

            <div>
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Дополнительная информация о локации"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="organization" className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                Организация
              </Label>
              <Select
                placeholder="Выберите организацию"
                value={formData.organization_id || ''}
                onChange={(e) => setFormData({ ...formData, organization_id: e.target.value || undefined })}
              >
                <SelectOption value="">Общая локация (все организации)</SelectOption>
                {organizations?.map((org: { id: string; name: string }) => (
                  <SelectOption key={org.id} value={org.id}>
                    {org.name}
                  </SelectOption>
                ))}
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Оставьте пустым для общей локации, доступной всем организациям
              </p>
            </div>
          </div>

          {/* Точка продаж и подразделения точки */}
          {location ? (
            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Store className="h-4 w-4 text-gray-500" />
                <Label>Точка продаж и подразделения</Label>
              </div>

              <div>
                <Label htmlFor="forecast-dept-id">
                  Точка продаж (iiko / aqniet) — UUID
                </Label>
                <Input
                  id="forecast-dept-id"
                  value={forecastDeptId}
                  onChange={(e) => setForecastDeptId(e.target.value)}
                  placeholder="d576acd8-8d5e-450c-8851-5986258b0c6e"
                  className={errors.forecast_department_id ? 'border-red-500' : ''}
                />
                {errors.forecast_department_id && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.forecast_department_id}
                  </p>
                )}
                {forecastMapping?.source === 'organization' && !forecastDeptId && (
                  <p className="text-xs text-amber-600 mt-1">
                    Унаследовано от организации:{' '}
                    {forecastMapping.forecast_department_name ||
                      forecastMapping.forecast_department_id}
                    . Заполните поле, чтобы привязать точку к локации напрямую.
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  UUID отдела из сервиса прогноза продаж (aqniet.site). По нему
                  подтягивается выручка этой точки в отчётах и Smart Schedule.
                </p>
              </div>

              <div>
                <Label htmlFor="forecast-dept-name">Название точки продаж</Label>
                <Input
                  id="forecast-dept-name"
                  value={forecastDeptName}
                  onChange={(e) => setForecastDeptName(e.target.value)}
                  placeholder="Tary Semey"
                />
              </div>

              <div>
                <Label>Подразделения этой точки</Label>
                <p className="text-xs text-gray-500 mb-2">
                  По этим подразделениям считается штат и ФОТ точки в отчётах
                  {formData.organization_id
                    ? ' (показаны подразделения выбранной организации)'
                    : ' (организация не выбрана — показаны все подразделения)'}
                </p>
                <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                  {(departmentsForPick?.data ?? []).map((dept) => (
                    <label
                      key={dept.id}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDeptIds.has(dept.id)}
                        onChange={() => toggleDepartment(dept.id)}
                        className="h-4 w-4"
                      />
                      <span className="flex-1">{dept.name}</span>
                      {typeof dept.employee_count === 'number' && (
                        <span className="text-xs text-gray-400">
                          {dept.employee_count} чел.
                        </span>
                      )}
                    </label>
                  ))}
                  {(departmentsForPick?.data ?? []).length === 0 && (
                    <p className="text-sm text-gray-400 px-1 py-2">
                      Подразделения не найдены
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Выбрано: {selectedDeptIds.size}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              Точку продаж и подразделения можно будет привязать после создания
              локации (откройте её на редактирование).
            </p>
          )}

          {/* Координаты */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              <Label>Координаты</Label>
            </div>
            
            {/* Кнопки выбора координат */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsMapModalOpen(true)}
                className="flex-1 sm:flex-none"
              >
                <Map className="h-3 w-3 mr-2" />
                Выбрать на карте
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGetCurrentLocation}
                disabled={geoLoading}
                className="flex-1 sm:flex-none"
              >
                {geoLoading ? (
                  <LoadingSpinner className="h-3 w-3 mr-2" />
                ) : (
                  <Navigation className="h-3 w-3 mr-2" />
                )}
                Мое местоположение
              </Button>
            </div>

            {/* Ссылка 2GIS */}
            <div>
              <Label htmlFor="gis-link" className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-gray-500" />
                Ссылка 2GIS
              </Label>
              <Input
                id="gis-link"
                value={gisLink}
                onChange={(e) => handleGisLinkChange(e.target.value)}
                placeholder="https://2gis.kz/shymkent/..."
                className={gisLinkStatus === 'error' ? 'border-red-500' : ''}
              />
              <p className="text-xs text-gray-500 mt-1">
                Вставьте ссылку из 2GIS — координаты будут заполнены автоматически
              </p>
              {/* Статус парсинга */}
              {gisLinkStatus === 'success' && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <span>✓</span>
                  {coordinatesSource === '2gis'
                    ? 'Координаты заполнены из 2GIS'
                    : 'Координаты распознаны (используются координаты из другого источника)'
                  }
                </p>
              )}
              {gisLinkStatus === 'error' && (
                <p className="text-xs text-red-500 mt-1">
                  Не удалось извлечь координаты из ссылки 2GIS. Проверьте формат ссылки.
                </p>
              )}
            </div>

            {/* Информация об ошибке геолокации */}
            {geoError && (
              <div className={`rounded-lg p-3 ${
                permissionDenied
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-amber-50 border border-amber-200'
              }`}>
                <p className={`text-sm ${permissionDenied ? 'text-red-800' : 'text-amber-800'}`}>
                  <strong>{permissionDenied ? '🚫' : 'ℹ️'} {permissionDenied ? 'Доступ запрещен:' : 'Примечание:'}</strong> {geoError}
                </p>
                {permissionDenied ? (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-red-700 font-medium">Как разрешить доступ к геолокации:</p>
                    <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5 ml-1">
                      <li>Нажмите на иконку замка/информации в адресной строке браузера</li>
                      <li>Найдите настройки "Местоположение" и выберите "Разрешить"</li>
                      <li>Обновите страницу и попробуйте снова</li>
                    </ul>
                    <p className="text-xs text-red-700 mt-2">
                      Или используйте кнопку "Выбрать на карте" для указания координат.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-amber-700 mt-1">
                    Вы можете вручную указать координаты или выбрать точку на карте.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude">Широта *</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="0.000001"
                  value={formData.latitude}
                  onChange={(e) => handleManualCoordinateChange('latitude', parseFloat(e.target.value))}
                  className={errors.latitude ? 'border-red-500' : ''}
                />
                {errors.latitude && (
                  <p className="text-sm text-red-500 mt-1">{errors.latitude}</p>
                )}
              </div>

              <div>
                <Label htmlFor="longitude">Долгота *</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="0.000001"
                  value={formData.longitude}
                  onChange={(e) => handleManualCoordinateChange('longitude', parseFloat(e.target.value))}
                  className={errors.longitude ? 'border-red-500' : ''}
                />
                {errors.longitude && (
                  <p className="text-sm text-red-500 mt-1">{errors.longitude}</p>
                )}
              </div>
            </div>
          </div>

          {/* Настройки контроля */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-500" />
              <Label>Настройки контроля</Label>
            </div>

            <div>
              <Label htmlFor="radius">Радиус действия (метры) *</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="radius"
                  type="range"
                  min="10"
                  max="1000"
                  step="10"
                  value={formData.radius_meters}
                  onChange={(e) => setFormData({ ...formData, radius_meters: parseInt(e.target.value) })}
                  className="flex-1"
                />
                <span className="text-sm font-medium w-16 text-right">
                  {formData.radius_meters} м
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="gps-validation">Требовать GPS проверку</Label>
              <Switch
                id="gps-validation"
                checked={formData.requires_gps_validation ?? false}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, requires_gps_validation: checked })
                }
              />
            </div>

            {formData.requires_gps_validation && (
              <div>
                <Label htmlFor="max-deviation">Погрешность GPS (метры)</Label>
                <Input
                  id="max-deviation"
                  type="number"
                  min="5"
                  max="1000"
                  value={formData.max_gps_deviation_meters}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_gps_deviation_meters: parseInt(e.target.value),
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Добавляется к радиусу действия. Итого: {formData.radius_meters + (formData.max_gps_deviation_meters || 25)} м
                </p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="is-active">Активна</Label>
              <Switch
                id="is-active"
                checked={formData.is_active ?? true}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_active: checked })
                }
              />
            </div>
          </div>
          </form>
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          {errors.submit && (
            <p className="text-sm text-red-500 flex-1">{errors.submit}</p>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={isLoading} onClick={handleSubmit}>
            {isLoading && <LoadingSpinner className="mr-2 h-4 w-4" />}
            {location ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Модальное окно с картой */}
      <Dialog open={isMapModalOpen} onOpenChange={handleCancelLocationSelection}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader>
            <DialogTitle>Выбор локации на карте</DialogTitle>
            <DialogDescription>
              Кликните на карте, чтобы выбрать точку для новой локации, затем нажмите "Выбрать" для подтверждения
            </DialogDescription>
          </DialogHeader>


          {/* Карта */}
          <div className="flex-1 min-h-0 px-6">
            <div className="h-[500px] w-full rounded-lg overflow-hidden border">
              <LazyLocationMap
                latitude={tempCoordinates?.latitude ?? (formData.latitude || 43.238949)}
                longitude={tempCoordinates?.longitude ?? (formData.longitude || 76.889709)}
                radius={formData.radius_meters}
                isInteractive={true}
                onLocationSelect={handleMapLocationSelect}
                onRadiusChange={handleRadiusChange}
                height="100%"
              />
            </div>
          </div>

          {/* Информация о выбранных координатах */}
          {tempCoordinates && (
            <div className="flex-shrink-0 px-6 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-800">
                  <strong>Выбраны координаты:</strong>
                </div>
                <div className="text-sm text-blue-700">
                  Широта: {tempCoordinates.latitude.toFixed(6)}, Долгота: {tempCoordinates.longitude.toFixed(6)}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancelLocationSelection}>
              Отмена
            </Button>
            <Button 
              type="button" 
              onClick={handleConfirmLocationSelection}
              disabled={!tempCoordinates}
            >
              Выбрать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};